import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { ArrowLeft, Globe2, Settings2, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/Dialog'
import { Field, Input, Textarea } from '@/components/ui/Field'
import { Dialog } from '@/components/ui/Dialog'
import { Card, EmptyState, LoadingPanel, Notice } from '@/components/ui/misc'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { api, ApiError, queryString } from '@/lib/api'
import { formatDate } from '@/lib/utils'
import { Pagination } from '../admin/MasterDataTable'
import { RecordDetailModal } from '../database/RecordDetailModal'
import { InviteDialog, MembersDialog } from './dialogs'
import { AvatarStack, InviteButton } from './MemberList'
import { ProjectReviewPill, RoleBadge, StatusPill } from './WorkspaceOverview'
import { WorkspaceFilters, type WorkspaceFilterState, EMPTY_WORKSPACE_FILTERS } from './WorkspaceFilters'
import type { WorkspaceDetail, WorkspaceRecord } from './types'

const PAGE_SIZE = 12

export function WorkspaceDetailPage() {
  const { workspaceId = '' } = useParams()
  const navigate = useNavigate()
  const { user } = useCurrentUser()

  const [filters, setFilters] = useState<WorkspaceFilterState>(EMPTY_WORKSPACE_FILTERS)
  const [page, setPage] = useState(1)
  // The overview links straight to a record, so honour ?record= on arrival.
  const [searchParams] = useSearchParams()
  const [openRecordId, setOpenRecordId] = useState<string | null>(searchParams.get('record'))
  const [inviting, setInviting] = useState(false)
  const [showMembers, setShowMembers] = useState(false)
  const [editing, setEditing] = useState(false)

  const detail = useQuery({
    queryKey: ['workspace', workspaceId],
    queryFn: () => api.get<WorkspaceDetail>(`/workspaces/${workspaceId}`),
  })

  const query = queryString({
    page,
    pageSize: PAGE_SIZE,
    ...(filters.kind ? { kind: filters.kind } : {}),
    ...(filters.fiber.length ? { fiber: filters.fiber.join(',') } : {}),
    ...(filters.dyeSource.length ? { dyeSource: filters.dyeSource.join(',') } : {}),
    ...(filters.mordant.length ? { mordant: filters.mordant.join(',') } : {}),
  })

  const records = useQuery({
    queryKey: ['workspace', workspaceId, 'records', query],
    queryFn: () =>
      api.get<{ records: WorkspaceRecord[]; total: number }>(
        `/workspaces/${workspaceId}/records${query}`,
      ),
    placeholderData: keepPreviousData,
  })

  const facets = useQuery({
    queryKey: ['workspace', workspaceId, 'facets', query],
    queryFn: () => api.get(`/workspaces/${workspaceId}/facets${query}`),
    placeholderData: keepPreviousData,
  })

  if (detail.isLoading) return <LoadingPanel label="Loading project" />

  if (detail.isError) {
    return (
      <div className="py-6">
        <EmptyState
          title="Project not available"
          description="It may have been deleted, or you are no longer a member."
          action={
            <Link to="/projects">
              <Button variant="secondary">Back to projects</Button>
            </Link>
          }
        />
      </div>
    )
  }

  const workspace = detail.data!.workspace
  const members = detail.data!.members

  return (
    <div>
      <Card className="mb-5 flex items-center gap-4 px-4 py-4">
        <button
          type="button"
          onClick={() => navigate('/projects')}
          aria-label="Back to projects"
          className="flex size-8 shrink-0 items-center justify-center rounded-full border border-line text-ink-muted hover:bg-paper-muted hover:text-ink"
        >
          <ArrowLeft className="size-4" />
        </button>

        <div className="min-w-0 flex-1">
          <h1 className="flex items-center gap-2 font-display text-[22px] font-semibold leading-tight text-ink">
            <span className="truncate">{workspace.name}</span>
            <RoleBadge isOwner={workspace.isOwner} />
            <ProjectReviewPill status={workspace.reviewStatus} />
          </h1>
          {workspace.description && (
            <p className="mt-0.5 truncate text-[13px] text-ink-muted">{workspace.description}</p>
          )}
        </div>

        <button type="button" onClick={() => setShowMembers(true)} className="shrink-0">
          <AvatarStack members={detail.data!.memberPreview} total={workspace.memberCount} />
        </button>

        {workspace.isOwner && <InviteButton onClick={() => setInviting(true)} />}

        <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
          <Settings2 className="size-3.5" />
          Project settings
        </Button>
      </Card>

      <ProjectPublication workspace={workspace} />

      <div className="flex gap-6">
        <WorkspaceFilters
          facets={facets.data as never}
          filters={filters}
          onChange={(next) => {
            setFilters(next)
            setPage(1)
          }}
        />

        <div className="min-w-0 flex-1">
          {records.isLoading ? (
            <LoadingPanel label="Loading records" />
          ) : records.data?.records.length === 0 ? (
            <EmptyState
              title="No records here yet"
              description="Open one of your own records and choose “Save to project” to pool it with this group."
            />
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                {records.data?.records.map((record) => (
                  <WorkspaceRecordCard
                    key={record.id}
                    record={record}
                    workspaceId={workspaceId}
                    canRemove={
                      workspace.reviewStatus !== 'in_review' &&
                      (workspace.isOwner || record.addedByName === user?.name)
                    }
                    onOpen={() => setOpenRecordId(record.id)}
                  />
                ))}
              </div>

              <div className="flex items-center justify-between pt-4">
                <Pagination
                  page={page}
                  totalPages={Math.ceil((records.data?.total ?? 0) / PAGE_SIZE)}
                  onChange={setPage}
                />
                <p className="text-[12px] text-ink-muted">
                  Showing {records.data?.records.length} of {records.data?.total} records
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      <RecordDetailModal
        recordId={openRecordId}
        onOpenChange={(open) => !open && setOpenRecordId(null)}
        onOpenRecord={setOpenRecordId}
      />

      <InviteDialog workspace={workspace} open={inviting} onOpenChange={setInviting} />

      <MembersDialog
        workspace={workspace}
        members={members}
        open={showMembers}
        onOpenChange={setShowMembers}
        onInvite={() => setInviting(true)}
      />

      <WorkspaceSettingsDialog
        workspace={workspace}
        open={editing}
        onOpenChange={setEditing}
        onDeleted={() => navigate('/projects')}
      />
    </div>
  )
}

/**
 * Sending the project for approval publishes every experiment in it at once.
 * Published copies are frozen for the team; the originals stay editable here and
 * only reach the Public Database again when the project is resubmitted.
 */
function ProjectPublication({ workspace }: { workspace: WorkspaceDetail['workspace'] }) {
  const queryClient = useQueryClient()

  const act = useMutation({
    mutationFn: (action: 'submit' | 'withdraw') =>
      api.post<{ experiments?: number }>(`/workspaces/${workspace.id}/${action}`),
    onSuccess: (result, action) => {
      queryClient.invalidateQueries({ queryKey: ['workspace', workspace.id] })
      queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      toast.success(
        action === 'submit'
          ? `Project sent for approval with ${result.experiments} experiment${result.experiments === 1 ? '' : 's'}.`
          : 'Project withdrawn from review.',
      )
    },
    onError: (error: ApiError) => toast.error(error.body.error),
  })

  const submitButton = (label: string) =>
    workspace.isOwner && (
      <Button size="sm" loading={act.isPending} onClick={() => act.mutate('submit')}>
        <Globe2 className="size-3.5" />
        {label}
      </Button>
    )

  if (workspace.reviewStatus === 'in_review') {
    return (
      <div className="mb-5 flex items-start gap-4">
        <div className="flex-1">
          <Notice tone="caution" title="Awaiting approval">
            Submitted {workspace.submittedAt ? formatDate(workspace.submittedAt) : ''}. A super user or
            admin reviews every experiment in this project and approves them in one go. Records in
            the project cannot be edited, added or removed until then.
          </Notice>
        </div>
        {workspace.isOwner && (
          <Button variant="secondary" size="sm" loading={act.isPending} onClick={() => act.mutate('withdraw')}>
            Withdraw
          </Button>
        )}
      </div>
    )
  }

  if (workspace.reviewStatus === 'rejected') {
    return (
      <div className="mb-5 flex items-start gap-4">
        <div className="flex-1">
          <Notice tone="brand" title="Changes requested">
            {workspace.rejectionReason}
          </Notice>
        </div>
        {submitButton('Resubmit project')}
      </div>
    )
  }

  if (workspace.reviewStatus === 'published') {
    return (
      <div className="mb-5 flex items-start gap-4">
        <div className="flex-1">
          <Notice tone="positive" title={`Published ${workspace.publishedAt ? formatDate(workspace.publishedAt) : ''}`}>
            The experiments are in the Public Database as copies that only super users and admins
            can edit. You can keep editing the originals here; changes reach the Public Database
            when you submit the project again.
          </Notice>
        </div>
        {submitButton('Publish changes')}
      </div>
    )
  }

  if (!workspace.isOwner) return null

  return (
    <Card className="mb-5 flex items-center gap-4 px-4 py-3">
      <p className="flex-1 text-[13px] text-ink-soft">
        <span className="font-semibold text-ink">Publish this project.</span> Send every finished
        experiment in it for approval at once. A single approval publishes them all.
      </p>
      {submitButton('Submit for approval')}
    </Card>
  )
}

function WorkspaceRecordCard({
  record,
  workspaceId,
  canRemove,
  onOpen,
}: {
  record: WorkspaceRecord
  workspaceId: string
  canRemove: boolean
  onOpen: () => void
}) {
  const queryClient = useQueryClient()

  const remove = useMutation({
    mutationFn: () => api.delete(`/workspaces/${workspaceId}/records/${record.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace', workspaceId] })
      toast.success(`${record.code} removed from this project`)
    },
    onError: (error: ApiError) => toast.error(error.body.error),
  })

  const dated =
    record.experimentDate ?? (record.referenceYear !== null ? String(record.referenceYear) : null)

  return (
    <Card className="group flex overflow-hidden">
      <button
        type="button"
        onClick={onOpen}
        className="size-[108px] shrink-0 border-r border-line"
        style={{ backgroundColor: record.swatchHex ?? '#ddd0c2' }}
        aria-label={`Open ${record.title}`}
      />

      <div className="flex min-w-0 flex-1 flex-col justify-between p-3">
        <div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] font-semibold text-brand">{record.code}</span>
            <StatusPill status={record.status} />
          </div>
          {record.publishedCode && (
            <p className="flex items-center gap-1 text-[10px] font-semibold text-positive">
              <Globe2 className="size-3" />
              Published as {record.publishedCode}
            </p>
          )}

          <button type="button" onClick={onOpen} className="block w-full text-left">
            <p className="truncate pt-0.5 font-display text-[15px] font-semibold text-ink">
              {record.title}
            </p>
          </button>

          <p className="truncate text-[11px] text-ink-muted">
            {[record.fiberName, record.dyeSourceName, dated].filter(Boolean).join(' · ')}
          </p>
        </div>

        <div className="flex items-end justify-between gap-2">
          <p className="truncate text-[11px] text-ink-faint">Added by {record.addedByName}</p>

          {canRemove && (
            <button
              type="button"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
              aria-label={`Remove ${record.code} from this project`}
              className="shrink-0 rounded p-1 text-ink-faint opacity-0 transition-opacity hover:bg-paper-muted hover:text-brand focus-visible:opacity-100 group-hover:opacity-100"
            >
              <Trash2 className="size-3.5" />
            </button>
          )}
        </div>
      </div>
    </Card>
  )
}

function WorkspaceSettingsDialog({
  workspace,
  open,
  onOpenChange,
  onDeleted,
}: {
  workspace: WorkspaceDetail['workspace']
  open: boolean
  onOpenChange: (open: boolean) => void
  onDeleted: () => void
}) {
  const queryClient = useQueryClient()
  const [name, setName] = useState(workspace.name)
  const [description, setDescription] = useState(workspace.description ?? '')
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/workspaces/${workspace.id}`, {
        name: name.trim(),
        description: description.trim() || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace', workspace.id] })
      queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      toast.success('Project updated')
      onOpenChange(false)
    },
    onError: (error: ApiError) => toast.error(error.body.error),
  })

  const remove = useMutation({
    mutationFn: () => api.delete(`/workspaces/${workspace.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      toast.success(`${workspace.name} deleted`)
      onDeleted()
    },
    onError: (error: ApiError) => toast.error(error.body.error),
  })

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={onOpenChange}
        title="Project settings"
        footer={
          <>
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              loading={save.isPending}
              disabled={!workspace.isOwner || name.trim().length < 2}
              onClick={() => save.mutate()}
            >
              Save changes
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Project name" required htmlFor="settings-name">
            <Input
              id="settings-name"
              value={name}
              disabled={!workspace.isOwner}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>

          <Field label="Description" htmlFor="settings-description">
            <Textarea
              id="settings-description"
              rows={3}
              value={description}
              disabled={!workspace.isOwner}
              onChange={(event) => setDescription(event.target.value)}
            />
          </Field>

          {workspace.isOwner ? (
            <div className="border-t border-line pt-4">
              <p className="text-[13px] font-semibold text-ink">Delete this project</p>
              <p className="pb-2 text-[12px] text-ink-muted">
                Members lose access to the shared view. The records themselves stay with their
                authors and are not deleted.
              </p>
              <Button variant="outlineBrand" size="sm" onClick={() => setConfirmingDelete(true)}>
                <Trash2 className="size-3.5" />
                Delete project
              </Button>
            </div>
          ) : (
            <p className="text-[12px] text-ink-muted">
              Only the project owner can change these settings.
            </p>
          )}
        </div>
      </Dialog>

      <ConfirmDialog
        open={confirmingDelete}
        onOpenChange={setConfirmingDelete}
        title={`Delete ${workspace.name}?`}
        description="Every member loses access to the shared view. The records stay with their authors."
        confirmLabel="Delete project"
        loading={remove.isPending}
        onConfirm={() => remove.mutate()}
      />
    </>
  )
}
